import { useCallback, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { ClientOnly } from "vike-react/ClientOnly";
import { Button } from "#components/Button.tsx";
import Input from "#components/Input.tsx";
import Label from "#components/Label.tsx";
import Turnstile from "#components/Turnstile.tsx";
import { ThemeButtonVariant } from "#components/theme.ts";
import {
	handleStringiverseTRPCError,
	isStringiverseTRPCError,
} from "#lib/trpc.ts";
import { useRPC } from "#store/trpc.ts";
import AuthForm from "../AuthForm.tsx";

export default function LoginPage() {
	const client = useRPC();
	const [isLoading, setLoading] = useState(false);

	type LoginForm = Parameters<(typeof client)["auth"]["login"]["mutate"]>[0];
	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
		setError,
		clearErrors,
		setValue,
		getValues,
		resetField,
	} = useForm<LoginForm>({
		defaultValues: { "cf-turnstile-response": "" },
		mode: "onTouched",
	});
	const [formError, setFormError] = useState<string | null>(null);

	const setToken = useCallback(
		(token: string | null) => {
			clearErrors("cf-turnstile-response");
			token
				? setValue("cf-turnstile-response", token)
				: resetField("cf-turnstile-response");
		},
		[setValue, resetField, clearErrors],
	);

	const onSubmit: SubmitHandler<LoginForm> = useCallback(
		async (data) => {
			if (getValues("cf-turnstile-response") === "") {
				setError("cf-turnstile-response", {
					message: "Please complete the CAPTCHA challenge",
				});
				return;
			}

			try {
				setLoading(true);
				setFormError(null);
				await client.auth.login.mutate(data);
				window.location.href = "/dashboard";
			} catch (e: unknown) {
				setLoading(false);
				if (isStringiverseTRPCError(e)) {
					handleStringiverseTRPCError(e, setError, setFormError);
				} else {
					setError("email", { message: "An unknown error occurred" });
					setError("password", { message: "An unknown error occurred" });
				}
			}
		},
		[client, setError, getValues],
	);

	return (
		<AuthForm
			formError={formError}
			title="Agent Login"
			onSubmit={handleSubmit(onSubmit)}
		>
			<Label htmlFor="email">Agent Email:</Label>
			<Input
				className="w-full"
				type="email"
				placeholder="agent@example.com"
				autoComplete="email"
				maxLength={80}
				id="email"
				required
				{...register("email", {
					required: { value: true, message: "An email address is required" },
					minLength: {
						value: 4,
						message: "Email must be at least 4 characters",
					},
					maxLength: {
						value: 80,
						message: "Email must be at most 80 characters",
					},
				})}
				error={errors.email}
			/>
			<Label htmlFor="password">Agent Password:</Label>
			<Input
				className="w-full"
				type="password"
				placeholder="********"
				autoComplete="new-password"
				minLength={10}
				id="password"
				required
				{...register("password", {
					required: { value: true, message: "A password is required" },
					minLength: {
						value: 10,
						message: "Password must be at least 10 characters",
					},
					maxLength: {
						value: 100,
						message: "Password must be at most 100 characters",
					},
				})}
				error={errors.password}
			/>

			<ClientOnly>
				<Turnstile
					onTokenChange={setToken}
					className="mt-6"
					error={errors["cf-turnstile-response"]}
				/>
			</ClientOnly>

			<Button
				type="submit"
				className="w-full mt-2"
				disabled={!isValid}
				loading={isLoading}
				loadingText="Authenticating..."
			>
				Sign In
			</Button>
			<div className="flex flex-col md:grid md:grid-cols-2 gap-3">
				<Button
					href="/auth/register"
					variant={ThemeButtonVariant.Outline}
					className="w-full"
				>
					Become an Agent
				</Button>
				<Button
					href="/auth/forgot-password"
					variant={ThemeButtonVariant.Outline}
					className="w-full"
				>
					Forgot Password
				</Button>
			</div>
		</AuthForm>
	);
}
